import { inject, Injectable, signal } from '@angular/core';
import { BaseService, UserDeviceService, UserService } from '@easy-lib/modules/shared/services';
import { DeviceType, FeatureKeys, IFeatureItem, ThemType } from '../model/modal-slider.model';
import { combineLatest, filter, from, map, mergeMap, Observable, reduce, Subject, switchMap, take, tap } from 'rxjs';
import { DeviceTypeEnum } from '@easy-lib/modules/account-activity';
import { convertImageToBase64 } from '@easy-lib/modules/messages/services/message.tools';
import { SettingStore } from '@easy-lib/state/setting/setting.store';

@Injectable({
	providedIn: 'root'
})
export class ModalSliderService extends BaseService {
	userService = inject(UserService);
	userDeviceService = inject(UserDeviceService);
	settingStore = inject(SettingStore);
	showModal = signal(false);

	data = new Subject<IFeatureItem[]>();
	data$ = this.data.asObservable();
	getData$(key: FeatureKeys): Observable<IFeatureItem[]> {
		return combineLatest([this.settingStore.setting$, this.userDeviceService.deviceType$]).pipe(
			filter(([settings, deviceType]) => !!settings && deviceType !== DeviceTypeEnum.Unknown),
			take(1),
			switchMap(([settings, deviceType]) => {
				const url = `Features?Key=${key}&DeviceType=${deviceType}&ThemeType=${settings.lightTheme ? ThemType.Light : ThemType.Dark}`;
				return this.httpService.get<IFeatureItem[]>(this.mtsPath + this.apiUrls.easy + url);
			}),
			switchMap((items: IFeatureItem[]) => {
				return from(items).pipe(
					mergeMap(item => {
						return this.getMedia(item);
					})
				);
			}),
			reduce((acc: IFeatureItem[], curr: IFeatureItem) => {
				return [...acc, curr];
			}, []),
			tap(data => this.data.next(data.toSort((a, b) => a.pageNumber - b.pageNumber)))
		);
	}

	fetchMedia$ = this.data$.pipe(switchMap(from));
	getMedia(item): Observable<IFeatureItem> {
		return this.httpService
			.getBlob(`${this.mtsPath + this.apiUrls.easy}media/${item.mediaId}`, {
				responseType: 'blob'
			})
			.pipe(
				switchMap(res => convertImageToBase64(res)),
				map(res => {
					item.media = res;
					return item;
				})
			);
	}
	showSliderModal() {
		this.showModal.set(true);
	}

	closeSliderModal(id) {
		this.showModal.set(false);

		this.httpService
			.put(this.mtsPath + this.apiUrls.easy + 'Features', {
				body: {
					featureId: id,
					deviceType:
						this.userDeviceService.deviceType == DeviceTypeEnum.Mobile
							? DeviceType.Mobile
							: DeviceType.Desktop
				}
			})
			.subscribe();
	}
}
